import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Paperclip, Upload, Trash2, FileText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Reusable attachments list + uploader for tasks, leads, projects and announcements.
 * Files are stored in the existing Repository and visible there too.
 *
 * Props:
 *  - parentType: 'task' | 'lead' | 'project' | 'announcement'
 *  - parentId:   string (use a "draft-<uuid>" id for announcements before they exist)
 *  - canModify:  whether the current user may upload/delete (default true)
 *  - compact:    bool — denser layout for inline use
 *  - onChange:   optional callback(attachments[]) fired after every change
 */
export const RecordAttachments = ({ parentType, parentId, canModify = true, compact = false, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!parentType || !parentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attachments`, { params: { parent_type: parentType, parent_id: parentId } });
      setItems(res.data || []);
      onChange && onChange(res.data || []);
    } catch (e) {
      // Silent — parent might not exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [parentType, parentId]);

  const handlePick = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('parent_type', parentType);
      fd.append('parent_id', parentId);
      try {
        await axios.post(`${API}/attachments/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        okCount++;
      } catch (err) {
        toast.error(`Upload failed for "${f.name}": ${err.response?.data?.detail || 'error'}`);
      }
    }
    setUploading(false);
    if (okCount > 0) toast.success(`${okCount} file(s) attached`);
    if (fileInputRef.current) fileInputRef.current.value = '';
    load();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from this record?`)) return;
    try {
      await axios.delete(`${API}/attachments/${id}`);
      toast.success('Attachment removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await axios.get(`${API}/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <div data-testid={`attachments-${parentType}-${parentId}`} className={compact ? '' : 'space-y-2'}>
      <div className="flex items-center justify-between">
        <p className={`text-${compact ? 'xs' : 'sm'} font-medium text-muted-foreground flex items-center gap-1.5`}>
          <Paperclip className="h-3.5 w-3.5" /> Attachments {items.length > 0 && <span className="text-foreground">({items.length})</span>}
        </p>
        {canModify && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePick}
            disabled={uploading}
            data-testid={`attach-upload-btn-${parentType}`}
          >
            <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Add file'}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleUpload}
          className="hidden"
          data-testid={`attach-file-input-${parentType}`}
        />
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-1">
          {loading ? 'Loading…' : 'No files attached yet.'}
        </p>
      ) : (
        <ul className="divide-y rounded-md border bg-slate-50/50">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`attachment-row-${a.id}`}>
              <FileText className="h-4 w-4 text-blue-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate font-medium">{a.original_filename}</p>
                <p className="text-[11px] text-muted-foreground">
                  {a.owner_name} · {formatBytes(a.file_size)} · {a.created_at?.slice(0, 10)}
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => handleDownload(a)} data-testid={`attach-download-${a.id}`} title="Download">
                <Download className="h-3.5 w-3.5" />
              </Button>
              {canModify && (
                <Button type="button" variant="ghost" size="sm" onClick={() => handleDelete(a.id, a.original_filename)} data-testid={`attach-delete-${a.id}`} title="Remove">
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecordAttachments;
